import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, ShieldPlus } from "lucide-react";
import { toast } from "sonner";

const empty = { id: null, name: "", type: "private", notes: "", markup_percent: 20, is_active: true };

export default function Schemes() {
  const { can } = useAuth();
  const [schemes, setSchemes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/insurance-schemes").then((r) => setSchemes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setOpen(true); };
  const openEdit = (s) => { setForm({ id: s.id, name: s.name, type: s.type, notes: s.notes || "", markup_percent: s.markup_percent ?? 20, is_active: s.is_active !== false }); setOpen(true); };
  const save = async () => {
    if (!form.name) return toast.error("Scheme name is required");
    try {
      const payload = { name: form.name, type: form.type, notes: form.notes, markup_percent: Number(form.markup_percent) || 0, is_active: form.is_active };
      if (form.id) await api.put(`/insurance-schemes/${form.id}`, payload); else await api.post("/insurance-schemes", payload);
      toast.success("Scheme saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (s) => {
    try { await api.put(`/insurance-schemes/${s.id}`, { name: s.name, type: s.type, notes: s.notes || "", markup_percent: s.markup_percent ?? 20, is_active: s.is_active === false }); toast.success(s.is_active === false ? "Scheme activated" : "Scheme deactivated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const rows = schemes.filter((s) => filter === "all" ? true : filter === "active" ? s.is_active !== false : s.is_active === false);

  return (
    <div>
      <PageHeader title="Insurance Schemes" subtitle="NHIS and private HMO insurers — toggle active status to control which insurers are in use"
        actions={can("schemes.manage") && <Button data-testid="add-scheme-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Add Scheme</Button>} />
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="all" data-testid="scheme-tab-all">All</TabsTrigger>
          <TabsTrigger value="active" data-testid="scheme-tab-active">Active</TabsTrigger>
          <TabsTrigger value="inactive" data-testid="scheme-tab-inactive">Inactive</TabsTrigger>
        </TabsList>
        <TabsContent value={filter} className="mt-4">
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">Scheme</th><th className="py-2.5 px-3">Type</th><th className="py-2.5 px-3 text-right">Markup</th><th className="py-2.5 px-3">Notes</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3"></th></tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground"><ShieldPlus className="h-8 w-8 mx-auto mb-2 opacity-40" />No schemes</td></tr>}
                {rows.map((s) => (
                  <tr key={s.id} className={`border-t border-border hover:bg-muted/50 ${s.is_active === false ? "opacity-60" : ""}`}>
                    <td className="py-2 px-3 font-medium text-foreground">{s.name}</td>
                    <td className="py-2 px-3"><StatusBadge value={s.type === "nhis" ? "insurance" : "corporate"} label={s.type === "nhis" ? "NHIS" : "Private"} /></td>
                    <td className="py-2 px-3 text-right font-mono">{s.markup_percent ?? 0}%</td>
                    <td className="py-2 px-3 text-muted-foreground text-xs">{s.notes || "—"}</td>
                    <td className="py-2 px-3"><StatusBadge value={s.is_active === false ? "out" : "ok"} label={s.is_active === false ? "Inactive" : "Active"} /></td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {can("schemes.manage") && <>
                        <Button variant="ghost" size="sm" data-testid={`edit-scheme-${s.id}`} onClick={() => openEdit(s)}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="outline" size="sm" className="ml-1" data-testid={`toggle-scheme-${s.id}`} onClick={() => toggle(s)}>{s.is_active === false ? "Activate" : "Deactivate"}</Button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? "Edit Insurance Scheme" : "New Insurance Scheme"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="scheme-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="scheme-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="nhis">NHIS</SelectItem><SelectItem value="private">Private / HMO</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Markup (%)</Label><Input type="number" step="0.1" data-testid="scheme-markup-input" value={form.markup_percent} onChange={(e) => setForm((f) => ({ ...f, markup_percent: e.target.value }))} /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
            <div className="flex items-center gap-2 pt-1"><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="scheme-active-switch" /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-scheme-btn" onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
